/**
 * Hero.jsx — Homepage Hero
 *
 * Animations:
 *   - Illustration fades + scales in on load
 *   - VIRI'S appears first (200ms delay), CLAYWORKS second (580ms delay)
 *   - Both words enter with a playful off-axis tilt that settles into position
 *   - On scroll down: CLAYWORKS fades first, then VIRI'S
 *   - On scroll back up: VIRI'S reappears first, then CLAYWORKS
 */
import {
  motion,
  useMotionValue,
  useScroll,
  useTransform,
  useSpring,
  animate,
} from 'framer-motion';
import { useEffect } from 'react';
import { useHomepageContent } from '../services/firestore';

export default function Hero({ onExplore }) {
  const { content, loading } = useHomepageContent();
  const { scrollY } = useScroll();

  // — Smoothed scroll: adds a spring so scroll-driven motion feels slower —
  const smoothScroll = useSpring(scrollY, { stiffness: 40, damping: 18 });

  // — Per-word entrance opacity (0 → 1 on mount) —
  const virisEntrance = useMotionValue(0);
  const clayEntrance = useMotionValue(0);

  // — Per-word entrance X: slide in from the right (large → 0 on mount) —
  const virisX = useMotionValue(340);
  const clayX = useMotionValue(420);

  // — Per-word rotate (tilted → settled on mount) —
  const virisRotate = useMotionValue(7);
  const clayRotate = useMotionValue(-5);

  // — Illustration parallax: drifts up at 20% of scroll speed —
  const illustrationY = useTransform(smoothScroll, [0, 600], [0, -50]);

  // — Scroll-based opacity: CLAYWORKS fades first, VIRI'S second —
  const virisScrollOpacity = useTransform(
    smoothScroll,
    [0, 160, 280],
    [1, 1, 0],
  );
  const clayScrollOpacity = useTransform(smoothScroll, [0, 80, 180], [1, 1, 0]);

  // — Scroll-based X: words slide right as they disappear —
  const virisScrollX = useTransform(smoothScroll, [160, 280], [0, 420]);
  const clayScrollX = useTransform(smoothScroll, [80, 180], [0, 380]);

  // — Combined opacity = entrance × scroll —
  const virisOpacity = useTransform(
    [virisEntrance, virisScrollOpacity],
    ([entry, scroll]) => entry * scroll,
  );
  const clayOpacity = useTransform(
    [clayEntrance, clayScrollOpacity],
    ([entry, scroll]) => entry * scroll,
  );

  // — Combined X = entrance slide-in + scroll slide-out —
  const virisCombinedX = useTransform(
    [virisX, virisScrollX],
    ([entrance, scroll]) => entrance + scroll,
  );
  const clayCombinedX = useTransform(
    [clayX, clayScrollX],
    ([entrance, scroll]) => entrance + scroll,
  );

  // — Entrance animation: slowed to match ClassGrid word speed —
  useEffect(() => {
    const ease = [0.22, 1, 0.36, 1];
    const t1 = setTimeout(() => {
      animate(virisEntrance, 1, { duration: 1.2, ease });
      animate(virisX, 0, { duration: 1.3, ease });
      animate(virisRotate, 2.5, { duration: 1.3, ease });
    }, 350);
    const t2 = setTimeout(() => {
      animate(clayEntrance, 1, { duration: 1.2, ease });
      animate(clayX, 0, { duration: 1.3, ease });
      animate(clayRotate, -1.5, { duration: 1.3, ease });
    }, 750);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="hero-section">
      {/* ── Left — pottery illustration ─────────────────────────────────── */}
      <div className="hero-illustration-col">
        {/* Parallax wrapper: drifts up at ~20% of scroll speed */}
        <motion.div
          style={{
            y: illustrationY,
            height: '100%',
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <motion.img
            src={content.heroImageUrl || '/Main Pottery Illustration.png'}
            alt="Viridiana at the pottery wheel"
            className="hero-illustration-img"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1, y: [0, -10, 0] }}
            transition={{
              opacity: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
              scale: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
              y: {
                duration: 4.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.2,
              },
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </motion.div>
      </div>

      {/* ── Right — headline + content ──────────────────────────────────── */}
      <div className="hero-content-col">
        {/* Staggered animated headline */}
        <div
          className="hero-headline"
          style={{ lineHeight: 0.88, marginBottom: 72, userSelect: 'none' }}
        >
          <motion.div
            className="hero-headline-viris"
            style={{
              fontFamily: "'Titan One', sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(3rem, 6vw, 5.8rem)',
              color: '#1a1208',
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              paddingLeft: '4%',
              display: 'block',
              opacity: virisOpacity,
              x: virisCombinedX,
              rotate: virisRotate,
            }}
          >
            Viri&#8217;s
          </motion.div>

          <motion.div
            className="hero-headline-clayworks"
            style={{
              fontFamily: "'Titan One', sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(2.2rem, 4.5vw, 4.2rem)',
              color: '#1a1208',
              letterSpacing: '-0.03em',
              textTransform: 'uppercase',
              marginTop: '-0.05em',
              display: 'block',
              opacity: clayOpacity,
              x: clayCombinedX,
              rotate: clayRotate,
            }}
          >
            Clayworks
          </motion.div>
        </div>

        {/* Tag + subtitle + buttons */}
        <div
          className="hero-lower-content"
          style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.8s ease' }}
        >
          {/* Tag label */}
          <p
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: '1rem',
              color: '#9C7A3C',
              marginBottom: 12,
              letterSpacing: '0.01em',
            }}
          >
            {content.heroTagline}
          </p>

          {/* Subtitle */}
          <p
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(1.05rem, 1.8vw, 1.25rem)',
              color: '#1a1208',
              lineHeight: 1.5,
              maxWidth: 420,
              marginBottom: 36,
            }}
          >
            {content.heroHeadline}
          </p>

          {/* CTA buttons */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {/* Primary — amber pill */}
            <button
              onClick={onExplore}
              style={{
                fontFamily: "'Raleway', sans-serif",
                fontWeight: 700,
                fontSize: '1rem',
                background: '#E8A020',
                color: '#fff',
                border: 'none',
                borderRadius: 50,
                padding: '16px 36px',
                cursor: 'pointer',
                transition:
                  'background 0.2s ease, transform 0.18s ease, box-shadow 0.18s ease',
                letterSpacing: '0.01em',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#d4911a';
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow =
                  '0 8px 24px rgba(232, 160, 32, 0.38)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#E8A020';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {content.heroButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
